import { Component, OnInit, ElementRef, ViewChild, Input } from '@angular/core';
import { CasingTypeModel, WellSegmentModel, CasingType } from "../../../new-casing-plan/models/casing-plan.model";
import { CasingPlan2dDetailBaseComponent } from '../detail/casing-plan-2d-detail-base.component';
import { DiagonalDetailsDrawObject } from "./diagonal-details-draw-object.model";
import { CanvasMissingData, CanvasModel } from "../canvas-object.model";

@Component({
    selector: 'casing-plan-2d-detail-directional',
    templateUrl: './casing-plan-2d-detail.component.html',
    styleUrls: ['./casing-plan-2d-detail.component.scss', './casing-plan-2d-detail-directional.component.scss'],
    standalone: false
})

export class CasingPlan2dDetailDirectionalComponent extends CasingPlan2dDetailBaseComponent implements OnInit{
    @ViewChild('canvas', { static: true }) canvasRef: ElementRef;
    canvasModel: CanvasModel = new CanvasModel();
    @Input() segment: WellSegmentModel;                  //should be input - current segment displayed in diagram
    firstKickpoint: any;                        //first kickpoint found in string (number or undefined)
    canvasEdgePaddingPx: number = 5;            //stop drawing slightly before edge
    halfCasingWidthPx: number = 30;             //standard 1/2 width of casing in pixels
    curveTopPx: number = 0;                     //start of curve in pixels from TOP
    curveEndPx: number = 0;                     //end of curve in pixels from LEFT
    canvasCenterOffset: number = 100;           //offset from center to make the kickpoint angle
    cementLineWidthPx: number = 20;             //width of cement line in pixels

    boreholeWidth: number;                      //width of borehole in vertical axis
    canvasCenterY: number;                      //vertical center of the directions string
    diffRightCementHeight: number;              //height to offset right string based on diagram agnle of 22.5 degrees
    diffRightBoreholeHeight: number;    
    diffRightPackerHeight: number;
    diffRightBasketHeight: number;
    verticalTextOffset: number = 4;             //vertical offset to get text in middle of location & leader
    leading: number = 15;                       //line height (where used) in pixels
    drawObject: DiagonalDetailsDrawObject = {};
    
    constructor() {
        super();
    }
    ngOnInit() {

        this.leftArrow.src = "../../../../../assets/images/SVG/arrow-left.svg";
        this.rightArrow.src = "../../../../../assets/images/SVG/arrow-right.svg";
        this.canvasModel.canvasRef = this.canvasRef;
        this.firstKickpoint = this.getFirstSegmentKickpoint();

        if (this.firstKickpoint) {
            //set component vars
            this.setVariables();
            //draw canvas objects
            this.updateCanvas();

        } else {
            let missingData = new CanvasMissingData(this.canvasModel, this.dataMissingError);

            //add it to the canvas
            missingData.draw();
        }

    }

    setVariables(): void {
        this.bottomCanvasMarginPx = 200;         //canvas bottom margin in pixels - space for curve, shoes and bottom info box
        //canvas H/W minus some padding
        this.canvasHeight = this.canvasRef.nativeElement.height - (2 * this.canvasEdgePaddingPx) - 20; 
        this.canvasWidth = this.canvasRef.nativeElement.width - this.canvasEdgePaddingPx;
        //set factor for converting feet to pixels
        this.setfeetToPixelFactor(this.segment);
        //set canvas
        this.ctx = this.canvasRef.nativeElement.getContext('2d');
        //set center
        this.center = (this.canvasWidth / 2) - (this.canvasCenterOffset / 2);
        //set right casing pos
        this.rightCasing = this.center + this.halfCasingWidthPx;
        //set left casing pos
        this.leftCasing = this.center - this.halfCasingWidthPx;
        //set right borehole
        this.rightBorehole = this.rightCasing + this.diagramCasingWidth;
        //set left borehole
        this.leftBorehole = this.leftCasing - this.diagramCasingWidth;
        //set casing length
        this.casingLength = this.hasCasing(this.segment)
            ? ((this.segment.casingBottom - this.segment.casingTop) * this.factorFeetToPixels) + this.canvasMarginTop - this.bottomCanvasMarginPx
            : ((this.segment.boreholeBottom - this.segment.boreholeTop) * this.factorFeetToPixels) + this.canvasMarginTop - this.bottomCanvasMarginPx;
        //position of BEND top from TOP
        this.curveTopPx = this.canvasHeight - this.bottomCanvasMarginPx;

        //offset calcs from left string to right
        this.boreholeWidth = this.rightBorehole - this.leftBorehole;
        this.canvasCenterY = (this.canvasHeight * .5) + (this.canvasMarginTop * .5);
        this.diffRightCementHeight = 80 * Math.tan(this.degressToRadians(22.5));
        this.diffRightBoreholeHeight = 100 * Math.tan(this.degressToRadians(22.5));
        this.diffRightPackerHeight = 50 * Math.tan(this.degressToRadians(22.5));
        this.diffRightBasketHeight = 91 * Math.tan(this.degressToRadians(22.5));

        this.setDrawObject();
    }

    setDrawObject(): void {

        //maximum VERTICAL value for right side (vertical sides have different lengths)
        this.drawObject.verticalMaxRightPx = this.getVerticalPostionPx(this.firstKickpoint) - this.diffRightCementHeight;

        //calc cement values
        this.populateCementValues();

        //calc casing values
        this.populateCasingValues();

        //calc shoe values
        this.populateShoeValues();

        //calc collar values
        this.populateCollarValues();

        //calc packer values
        this.populatePackerValues();

        //calc basket values
        this.populateBasketValues();
    }

    populateBasketValues(): void {
        let verticalBasketOffset = 5;
        this.drawObject.validBasket = this.hasValidValue(this.segment.basketDepth, "number");
        if (this.drawObject.validBasket) {
            this.drawObject.basketBeforeKickPoint = this.segment.basketDepth < this.firstKickpoint;
            if (this.drawObject.basketBeforeKickPoint) {
                //vertical baskets
                this.drawObject.verticalBasketLeftPx = this.getVerticalPostionPx(this.segment.basketDepth) - verticalBasketOffset;
                this.drawObject.verticalBasketRightPx = this.checkVerticalMaxRight(this.drawObject.verticalBasketLeftPx);
                //have to vertical offset the image by 5 if it's at the max value
                if (this.drawObject.verticalBasketRightPx == this.drawObject.verticalMaxRightPx) {
                    this.drawObject.verticalBasketRightPx -= verticalBasketOffset;
                }
            } else {
                //diagonal baskets
                this.drawObject.diagonalBasketLeftPx = this.canvasCenterY + this.getPostKickPointSegmentLength("basket");
                this.drawObject.diagonalBasketRightPx = this.canvasCenterY + this.getPostKickPointSegmentLength("basket") - this.diffRightBasketHeight;
            }
        }
    }

    populatePackerValues(): void {
        let vertticalPackerOffset = 15;
        this.drawObject.validPacker = this.hasValidValue(this.segment.packerDepth, "number");
        if (this.drawObject.validPacker) {
            this.drawObject.packerBeforeKickPoint = this.segment.packerDepth < this.firstKickpoint;
            if (this.drawObject.packerBeforeKickPoint) {
                //vertical packers
                this.drawObject.verticalPackerLeftPx = this.getVerticalPostionPx(this.segment.packerDepth) - vertticalPackerOffset;
                this.drawObject.verticalPackerRightPx = this.checkVerticalMaxRight(this.drawObject.verticalPackerLeftPx);
                //have to v offset the image by 15 if it's at the max value
                if (this.drawObject.verticalPackerRightPx == this.drawObject.verticalMaxRightPx) {
                    this.drawObject.verticalPackerRightPx -= vertticalPackerOffset;
                }
            } else {
                //diagonal packers
                this.drawObject.diagonalPackerLeftPx = this.canvasCenterY + this.getPostKickPointSegmentLength("packer");
                this.drawObject.diagonalPackerRightPx = this.canvasCenterY + this.getPostKickPointSegmentLength("packer") - this.diffRightPackerHeight;
            }
        }
    }

    populateCollarValues(): void {
        let verticalCollarXOffset = 5
        this.drawObject.validCollar = this.hasValidValue(this.segment.floatCollarDepth, "number");
        if (this.drawObject.validCollar) {
            this.drawObject.collarBeforeKickPoint = this.segment.floatCollarDepth < this.firstKickpoint;
            if (this.drawObject.collarBeforeKickPoint) {
                //vertical collars 
                this.drawObject.verticalCollarLeftPx = this.getVerticalPostionPx(this.segment.floatCollarDepth) - verticalCollarXOffset;
                this.drawObject.verticalCollarRightPx = this.checkVerticalMaxRight(this.drawObject.verticalCollarLeftPx);
                //have to v offset the image by 5 if it's at the max value
                if (this.drawObject.verticalCollarRightPx == this.drawObject.verticalMaxRightPx) {
                    this.drawObject.verticalCollarRightPx -= verticalCollarXOffset;
                }
            } else {
                //diagonal collars
                this.drawObject.diagonalCollarLeftPx = this.canvasCenterY + this.getPostKickPointSegmentLength("collar");
                this.drawObject.diagonalCollarRightPx = this.canvasCenterY + this.getPostKickPointSegmentLength("collar") - this.diffRightCementHeight;
            }
        }
    }

    /**
     * Shoes are aligned with the bottom of the CASING, as per Horizontal & Vertical Detail diagrams
     *  They do NOT use the input value for shoe depth!
     * */
    populateShoeValues(): void {
        /* shoes to align with casing bottom */
        this.drawObject.validShoe = this.hasValidValue(this.segment.casingShoeDepth, "number");
        //USE CASING for Shoe location!
        this.drawObject.casingEndsBeforeKickPoint = this.segment.casingBottom < this.firstKickpoint;
        //Use casing for shoe before kick point!
        this.drawObject.shoeBeforeKickPoint = this.drawObject.casingEndsBeforeKickPoint;
        //casing STOPS
        if (this.drawObject.casingEndsBeforeKickPoint) {
            //Vertical shoes
            this.drawObject.verticalShoeLeftStartPx = this.getVerticalPostionPx(this.segment.casingBottom) - this.shoeHW;
            this.drawObject.verticalShoeRightStartPx = this.checkVerticalMaxRight(this.drawObject.verticalCasingRightEndPx) - 20;
            //have to v offset the image by 20 if it's at the max value
            if (this.drawObject.verticalShoeRightStartPx == this.drawObject.verticalMaxRightPx) {
                this.drawObject.verticalShoeRightStartPx -= 20;
            }
        } else {
            //Diagonal Shoes
            this.drawObject.diagonalShoeLeftStartPx = this.canvasCenterY + this.getPostKickPointSegmentLength("casing");
            this.drawObject.diagonalShoeRightStartPx = this.canvasCenterY + this.getPostKickPointSegmentLength("casing") - this.diffRightCementHeight;
        }
    }

    populateCasingValues(): void {
        //CASING
        this.drawObject.casingStartsBeforeKickPoint = this.segment.casingTop < this.firstKickpoint;
        this.drawObject.casingEndsBeforeKickPoint = this.segment.casingBottom < this.firstKickpoint;

        //casing STARTS
        if (this.drawObject.casingStartsBeforeKickPoint) {
            //casing starts BEFORE kick point (casing ALWAYS starts at top)
            this.drawObject.verticalCasingLeftStartPx = this.canvasMarginTop;
            this.drawObject.verticalCasingRightStartPx = this.canvasMarginTop;
        } else {
            //casing start AFTER kick point
            this.drawObject.diagonalCasingLeftStartPx = this.getPostKickPointSegmentLength("casing", true);

        }
        //casing STOPS
        if (this.drawObject.casingEndsBeforeKickPoint) {
            //casing stops BEFORE kick point
            this.drawObject.verticalCasingLeftEndPx = this.getVerticalPostionPx(this.segment.casingBottom);
            this.drawObject.verticalCasingRightEndPx = this.checkVerticalMaxRight(this.drawObject.verticalCasingLeftEndPx);
        } else {
            //casing stops AFTER kick point
            this.drawObject.diagonalCasingLeftEndPx = this.getPostKickPointSegmentLength("casing");
        }
    }

    populateCementValues(): void {
        //CEMENT
        this.drawObject.cementStartsBeforeKickPoint = this.segment.cementTop < this.firstKickpoint;
        this.drawObject.cementEndsBeforeKickPoint = this.segment.cementBottom < this.firstKickpoint;

        //make sure the cement doesn't go through the top of the borehole string beginning
        let calcCementTop = this.segment.cementTop < this.segment.boreholeTop ? this.segment.boreholeTop : this.segment.cementTop;

        //cement STARTS before kick point
        if (this.drawObject.cementStartsBeforeKickPoint) {
            this.drawObject.verticalLeftStartPx = this.getVerticalPostionPx(calcCementTop);
            //make sure right side isn't too far down
            this.drawObject.verticalRightStartPx = this.checkVerticalMaxRight(this.drawObject.verticalLeftStartPx);
        } else {
            //starts AFTER kick point
            this.drawObject.diagonalLeftStartPx = this.getPostKickPointSegmentLength("cement", true);
        }

        //cement ENDS before kick point
        if (this.drawObject.cementEndsBeforeKickPoint) {
            //populate vertical END px
            this.drawObject.verticalLeftEndPx = this.getVerticalPostionPx(this.segment.cementBottom);
            //make sure right side isn't too far down
            this.drawObject.verticalRightEndPx = this.checkVerticalMaxRight(this.drawObject.verticalLeftEndPx);
        } else {
            //stops AFTER kp
            this.drawObject.diagonalLeftEndPx = this.getPostKickPointSegmentLength("cement");
        }
    }

    checkVerticalMaxRight(pixel: number): number {
       return pixel < this.drawObject.verticalMaxRightPx ? pixel : this.drawObject.verticalMaxRightPx;
    }

    getVerticalPostionPx(feet: number): number {
        const top = this.hasBorehole(this.segment) ? this.segment.boreholeTop : this.segment.casingTop;
        let topFeet = this.firstKickpoint - top;
        let topPixels = this.canvasCenterY - this.canvasMarginTop;
        let verticalPixelsPerFoot = topPixels / topFeet;

        return ((feet - top) * verticalPixelsPerFoot) + this.canvasMarginTop;
    }

    updateCanvas(): void {
        
        this.addWhiteBackground(this.canvasHeight + (2 * this.canvasEdgePaddingPx) + 20, this.canvasWidth);
        if (this.hasCement(this.segment)) {
            this.addCementBottom();
            this.addCement();    
        }
        if (this.hasCasing(this.segment)) {
            this.addCasingLabels();    
            this.addCasing();
        }     
        if (this.hasBorehole(this.segment)) {
            this.addBoreHoleLabels();
        }
        
        this.addSegmentBottom();

        if (this.hasBorehole(this.segment)) {
            this.addBoreHole();
        }
        
        this.addGround(this.segmentIndex);
        this.addDiameterArrows(this.segment);
        this.addShoes();
        this.addCollars();
        this.addPackers();
        this.addBaskets();
        this.addKickPointLabel();
        this.addDashedLines();
        
    }

    addKickPointLabel(): void {

        //kickpoint label
        this.ctx.beginPath();
        this.ctx.font = this.font;
        this.ctx.fillStyle = this.fontFileStyle;
        this.ctx.textAlign = "right";
        this.ctx.fillText("Kick Point", this.leftBorehole - this.labelPadding - this.groundLineWidth, this.canvasCenterY + this.verticalTextOffset);
        //second line to clear left edge of canvas!
        this.ctx.fillText("(not to scale)", this.leftBorehole - this.labelPadding - this.groundLineWidth, this.canvasCenterY + this.verticalTextOffset + 12);
        this.ctx.closePath();
        this.ctx.textAlign = "left";

        //kickpoint line
        this.ctx.beginPath();
        this.ctx.fillStyle = "#000000";
        this.ctx.lineWidth = 1;
        this.ctx.moveTo(this.leftBorehole, this.canvasCenterY);
        this.ctx.lineTo(this.leftBorehole - this.groundLineWidth, this.canvasCenterY);
        this.ctx.stroke();
        this.ctx.closePath();
    }

    /**
    * TODO: Can we add these calc's to the populate?
    * */
    addCasing(): void {


        //if starts before kp and ends after kp
        let postKickPointCasingLength = this.getPostKickPointSegmentLength("casing");
        let leftBottom = this.getLeftBottomByPostKPLength(postKickPointCasingLength);

        //if starts and ends after kp
        let leftBottomPostKPStart = this.getLeftBottomByPostKPLength(this.drawObject.diagonalCasingLeftStartPx);
        let leftBottomPostKPEnd = this.getLeftBottomByPostKPLength(this.drawObject.diagonalCasingLeftEndPx);

        this.ctx.beginPath();
        this.ctx.strokeStyle = "#000000";
        this.ctx.lineWidth = this.innerCasingLineWidth;

        //LEFT
        this.ctx.moveTo(this.leftCasing, this.canvasMarginTop);

        //casing starts BEFORE kick point
        if (this.drawObject.casingStartsBeforeKickPoint) {
            if (this.drawObject.casingEndsBeforeKickPoint) {
                //ends BEFORE kick point
                this.ctx.lineTo(this.leftCasing, this.drawObject.verticalCasingLeftEndPx);
            } else {
                //end AFTER kick point
                //draw to kick point
                this.ctx.lineTo(this.leftCasing, this.canvasCenterY - 4);
                //left to end of string
                this.ctx.lineTo(this.leftCasing + leftBottom, this.canvasCenterY + postKickPointCasingLength - 4);
            }
        } else {
            //Probably never get here
            //casing starts and ends AFTER kick point
            this.ctx.moveTo(this.leftCasing + leftBottomPostKPStart, this.canvasCenterY + this.drawObject.diagonalCasingLeftStartPx);
            this.ctx.lineTo(this.leftCasing + leftBottomPostKPEnd, this.canvasCenterY + this.drawObject.diagonalCasingLeftEndPx);
        }




        //RIGHT
        this.ctx.moveTo(this.rightCasing, this.canvasMarginTop);

        if (this.drawObject.casingStartsBeforeKickPoint) {
            if (this.drawObject.casingEndsBeforeKickPoint) {
                //ends BEFORE kick point
                this.ctx.lineTo(this.rightCasing, this.drawObject.verticalCasingRightEndPx);

            } else {
                //ends AFTER kick point
                //draw to kick point
                this.ctx.lineTo(this.rightCasing, this.drawObject.verticalMaxRightPx + 2);
                //left to end of string
                this.ctx.lineTo(this.rightCasing + leftBottom, this.canvasCenterY + postKickPointCasingLength - this.diffRightCementHeight + 4);
            }
        } else {
            //probably never get here
            //casing starts and ends AFTER kick point
            this.ctx.moveTo(this.rightCasing + leftBottomPostKPStart, this.canvasCenterY + this.drawObject.diagonalCasingLeftStartPx);
            this.ctx.lineTo(this.rightCasing + leftBottomPostKPEnd, this.canvasCenterY + this.drawObject.diagonalCasingLeftEndPx);
        }

        //line to kick point



        this.ctx.stroke();
        this.ctx.closePath();       
    }

    addCasingLabels(): void {

        this.ctx.font = this.font;
        this.ctx.fillStyle = this.fontFileStyle;
        this.ctx.fillText("Top of " + this.segment.casingDiameter + " IN. Casing: " + this.segment.casingTop + " FT.", this.rightBorehole + this.groundLineWidth + this.labelPadding, this.canvasMarginTop + 4);

        this.ctx.font = this.font;
        this.ctx.fillStyle = this.fontFileStyle;
        this.ctx.fillText(`Bottom of ${this.segment.casingDiameter} IN.Casing: ${this.segment.casingBottom} FT.`, this.rightBorehole - (this.groundLineWidth + this.labelPadding) + this.canvasCenterOffset, this.canvasHeight);
    }

    /**
     * TODO: Can we add these calc's to the populate?
     * */
    addBoreHole(): void {

        let postKickPointCementLength = this.getPostKickPointSegmentLength("borehole");
        let leftBottom = this.getLeftBottomByPostKPLength(postKickPointCementLength);

        //right
        this.ctx.beginPath();
        this.ctx.strokeStyle = "#000000";
        this.ctx.moveTo(this.rightBorehole, this.canvasMarginTop);
        this.ctx.lineWidth = 1;
        this.ctx.lineTo(this.rightBorehole, this.canvasCenterY - this.diffRightCementHeight);
        this.ctx.lineTo(this.leftBorehole + this.boreholeWidth + leftBottom - 2, this.canvasHeight - this.diffRightCementHeight - 3);
        this.ctx.stroke();
        this.ctx.closePath();
        
        //left
        this.ctx.beginPath();
        this.ctx.moveTo(this.leftBorehole, this.canvasMarginTop);
        this.ctx.lineWidth = 1;
        this.ctx.lineTo(this.leftBorehole, (this.canvasHeight * .5) + (this.canvasMarginTop * .5));
        this.ctx.lineTo(this.leftBorehole + leftBottom + 1 , this.canvasHeight + 2);
        this.ctx.stroke();
        this.ctx.closePath();
        
    }

    /**
    * TODO: Can we add these calc's to the populate?
    * */
    addBoreHoleLabels(): void {
        //top label
        // left label position depends on if we are drawing the left ground rectangle
        let topLabelLeftLocation: number = this.leftBorehole - this.labelPadding;
        if (this.segmentIndex == 0) {
            topLabelLeftLocation -= this.groundLineWidth;
        }

        this.ctx.font = this.font;
        this.ctx.fillStyle = this.fontFileStyle;
        this.ctx.textAlign = "right";
        this.ctx.fillText("Top of " + this.segment.boreholeDiameter + " IN. Hole:", topLabelLeftLocation, this.canvasMarginTop + 4);
        this.ctx.fillText(this.segment.boreholeTop + " FT.", topLabelLeftLocation, this.canvasMarginTop + 16);

        //bottom label
        this.ctx.font = this.font;
        this.ctx.fillStyle = this.fontFileStyle;
        if (this.segment.casingType.definition !== CasingType.MineString) {
            this.ctx.fillText("Bottom of " + this.segment.boreholeDiameter + " IN. Hole: " + this.segment.boreholeBottom + " FT.", this.leftBorehole - (this.groundLineWidth + this.labelPadding) + this.canvasCenterOffset, this.canvasHeight);
        } else {
            //draw hole bottom at casing if Mine String
            this.ctx.fillText("Bottom of " + this.segment.boreholeDiameter + " IN. Hole: " + this.segment.boreholeBottom + " FT.", this.leftBorehole - (this.groundLineWidth + this.labelPadding) + this.canvasCenterOffset, this.casingLength)
        }
    }

    /**
     * TODO: Can we add these calc's to the populate?
     * */
    addCement(): void {

        let verticalRightCenter = this.rightBorehole - (this.cementLineWidthPx * .5);
        let verticalLeftCenter = this.leftBorehole + (this.cementLineWidthPx * .5);
        //if starts before kp and ends after kp
        let postKickPointCementLength = this.getPostKickPointSegmentLength("cement");
        let leftBottom = this.getLeftBottomByPostKPLength(postKickPointCementLength);
        //if starts and ends after kp
        let leftBottomPostKPStart = this.getLeftBottomByPostKPLength(this.drawObject.diagonalLeftStartPx);
        let leftBottomPostKPEnd = this.getLeftBottomByPostKPLength(this.drawObject.diagonalLeftEndPx);

        //CEMENT TOP LABEL (only label top as of 3/15/19)
        let labelX = this.rightBorehole + this.labelPadding;
        if (!this.drawObject.cementStartsBeforeKickPoint) {
            labelX += leftBottom;
            this.ctx.beginPath();
            this.ctx.font = this.font;
            this.ctx.fillStyle = this.fontFileStyle;
            this.ctx.textAlign = "left";

            this.ctx.fillText("Top of Cement: " + this.segment.cementTop + " FT.", verticalRightCenter + leftBottomPostKPStart + this.halfCasingWidthPx - (.5 * this.cementLineWidthPx), this.canvasCenterY + this.drawObject.diagonalLeftStartPx - this.diffRightCementHeight + this.verticalTextOffset);
            
            this.ctx.closePath();


        } else {
            this.ctx.beginPath();
            this.ctx.font = this.font;
            this.ctx.fillStyle = this.fontFileStyle;
            this.ctx.textAlign = "left";
            if (this.drawObject.verticalLeftStartPx < this.canvasMarginTop + this.leading) {

                this.ctx.fillText("Top of Cement: " + this.segment.cementTop + " FT.", labelX + this.groundLineWidth, this.drawObject.verticalLeftStartPx + this.leading);
            } else {
                this.ctx.fillText("Top of Cement: " + this.segment.cementTop + " FT.", labelX, this.drawObject.verticalLeftStartPx);
            }
            this.ctx.closePath();
        }

        //LEFT
        this.ctx.beginPath();
        this.ctx.strokeStyle = this.cementFillColor;
        this.ctx.lineWidth = this.cementLineWidthPx;

        //cement starts BEFORE kick point
        if (this.drawObject.cementStartsBeforeKickPoint) {
            this.ctx.moveTo(verticalLeftCenter, this.drawObject.verticalLeftStartPx);
            if (this.drawObject.cementEndsBeforeKickPoint) {

                //cement ends BEFORE kick point
                this.ctx.lineTo(verticalLeftCenter, this.drawObject.verticalLeftEndPx);

            } else {
                //cement ends AFTER kick point

                //left to center
                this.ctx.lineTo(verticalLeftCenter, (this.canvasHeight * .5) + (this.canvasMarginTop * .5));

                //left to end of string
                this.ctx.lineTo(verticalLeftCenter + leftBottom, this.canvasCenterY + postKickPointCementLength);
            }
        } else {
            //starts end ends after kick point
            this.ctx.moveTo(verticalLeftCenter + leftBottomPostKPStart, this.canvasCenterY + this.drawObject.diagonalLeftStartPx);
            this.ctx.lineTo(verticalLeftCenter + leftBottomPostKPEnd, this.canvasCenterY + this.drawObject.diagonalLeftEndPx);
        }

        this.ctx.stroke();
        this.ctx.closePath();

        //RIGHT
        this.ctx.beginPath();

        if (this.drawObject.cementStartsBeforeKickPoint) {
            this.ctx.moveTo(verticalRightCenter, this.drawObject.verticalRightStartPx);
            if (this.drawObject.cementEndsBeforeKickPoint) {

                //cement ends BEFORE kick point
                this.ctx.lineTo(verticalRightCenter, this.drawObject.verticalRightEndPx);

            } else {
                //cement ends AFTER kick point

                //right to vert center
                this.ctx.lineTo(verticalRightCenter, this.canvasCenterY - this.diffRightCementHeight);

                //right to end of string
                this.ctx.lineTo(verticalRightCenter + leftBottom, this.canvasCenterY + postKickPointCementLength - this.diffRightCementHeight);
            }
        } else {
            //starts end ends after kick point
            this.ctx.moveTo(verticalRightCenter + leftBottomPostKPStart, this.canvasCenterY + this.drawObject.diagonalLeftStartPx - this.diffRightCementHeight);
            this.ctx.lineTo(verticalRightCenter + leftBottomPostKPEnd, this.canvasCenterY + this.drawObject.diagonalLeftEndPx - this.diffRightCementHeight);
        }

        /*
        let x1 = verticalLeftCenter + leftBottom;
        let y1 = this.canvasCenterY + postKickPointCementLength;
        let x2 = verticalRightCenter + leftBottom;
        let y2 = this.canvasCenterY + postKickPointCementLength - this.diffRightCementHeight;
        this.drawPoint(x1, y1);
        this.drawPoint(x2, y2);
        */

        this.ctx.stroke();
        this.ctx.closePath();

    }

    /**
    * TODO: Can we add these calc's to the populate?
    * */
    addCollars(): void{
        let horizontalCollarOffset = 5; //center on cement
        let postKickPointCollarLength = this.getPostKickPointSegmentLength("collar");
        let leftBottom = this.getLeftBottomByPostKPLength(postKickPointCollarLength);
        if (this.drawObject.validCollar) {
            if (this.drawObject.collarBeforeKickPoint) {
                //draw vertical collar
                
                this.addDirectionalVerticalCollar(this.leftCasing - (this.cementLineWidthPx + horizontalCollarOffset), this.drawObject.verticalCollarLeftPx, this.rightCasing - horizontalCollarOffset, this.drawObject.verticalCollarRightPx);
            } else {
                //draw diagonal collar
                this.addDiagonalCollars(this.leftCasing + leftBottom - this.cementLineWidthPx + 1, this.drawObject.diagonalCollarLeftPx, this.rightCasing + leftBottom, this.drawObject.diagonalCollarRightPx + 2);
            }
        }
    }

    /**
    * TODO: Can we add these calc's to the populate?
    * */
    addShoes(): void {
        
        let postKickPointShoeLength = this.getPostKickPointSegmentLength("casing");
        let leftBottom = this.getLeftBottomByPostKPLength(postKickPointShoeLength);

        if (this.drawObject.validShoe) {
            if (this.drawObject.shoeBeforeKickPoint) {
                //draw vertical shoe
                this.addDirectionalVerticalShoe(this.leftCasing - this.cementLineWidthPx, this.drawObject.verticalShoeLeftStartPx, this.rightCasing, this.drawObject.verticalShoeRightStartPx);
            } else {
                // 7/9/19 - hard code values left in there for 7/12/19 release
                //draw diagonal shoe
                this.addDiagonalShoe(this.leftCasing + leftBottom - 19, this.drawObject.diagonalShoeLeftStartPx - 21, this.rightCasing + leftBottom -8, this.drawObject.diagonalShoeRightStartPx - 13);
            }
        }
    }

    addBaskets(): void {
        let postKickPointBacketlength = this.getPostKickPointSegmentLength("basket");
        let leftBottom = this.getLeftBottomByPostKPLength(postKickPointBacketlength);
        if (this.drawObject.basketBeforeKickPoint) {
            //draw vertical baskets
            this.addDirectionalVerticalBasket(this.leftCasing - this.cementLineWidthPx, this.drawObject.verticalBasketLeftPx, this.rightCasing, this.drawObject.verticalBasketRightPx);
        } else {
            //draw diagonal baskets
            this.addDiagonalBasket(this.leftCasing + leftBottom - this.cementLineWidthPx + 1, this.drawObject.diagonalBasketLeftPx, this.rightCasing + leftBottom + (.5 * this.cementLineWidthPx) - 5, this.drawObject.diagonalBasketRightPx);
        } 
    }

    /**
    * TODO: Make this the standard for basket placement
    * @param leftX
    * @param leftY
    * @param rightX
    * @param rightY
    */
    addDirectionalVerticalBasket(leftX: number, leftY: number, rightX: number, rightY: number): void {
        let basketLeft = new Image();
        let basketRight = new Image();

        basketLeft.onload = () => {
            //img, x, y, width, height
            this.ctx.drawImage(basketLeft, leftX, leftY, this.shoeHW, this.shoeHW);
        };

        basketRight.onload = () => {
            //img, x, y, width, height
            this.ctx.drawImage(basketRight, rightX, rightY, this.shoeHW, this.shoeHW);
        };

        basketLeft.src = "../../../assets/images/SVG/basket-left.svg";
        basketRight.src = "../../../assets/images/SVG/basket-right.svg";
    }

    addDiagonalBasket(leftX: number, leftY: number, rightX: number, rightY: number): void {
        let basketLeft = new Image();
        let basketRight = new Image();
        
        basketLeft.onload = () => {
            //img, x, y, width, height
            this.ctx.drawImage(basketLeft, leftX, leftY, 26, 14);
        };

        basketRight.onload = () => {
            //img, x, y, width, height
            this.ctx.drawImage(basketRight, rightX, rightY, 14, 26);
        };

        basketLeft.src = "../../../assets/images/SVG/diagonal-basket-left.svg";
        basketRight.src = "../../../assets/images/SVG/diagonal-basket-right.svg";
    }

    /**
     * TODO: Can we add these calc's to the populate?
     * */
    addPackers(): void {
        let postKickPointPackerLength = this.getPostKickPointSegmentLength("packer");
        let leftBottom = this.getLeftBottomByPostKPLength(postKickPointPackerLength);

        if (this.drawObject.validPacker) {
            if (this.drawObject.packerBeforeKickPoint) {
                //draw vertical packer
                this.addDirectionalVerticalPacker(this.leftCasing, this.drawObject.verticalPackerLeftPx, this.rightCasing - 15, this.drawObject.verticalPackerRightPx);
            } else {
                //draw diagonal packer
                this.addDiagonalPacker(this.leftCasing + leftBottom + 2, this.drawObject.diagonalPackerLeftPx, this.rightCasing + leftBottom - 10, this.drawObject.diagonalPackerRightPx);
            }
        }

    }

    /**
     * TODO: Make this the standard 
     * @param leftX
     * @param leftY
     * @param rightX
     * @param rightY
     */
    addDirectionalVerticalPacker(leftX: number, leftY: number, rightX: number, rightY: number): void {
        let packer = new Image();
        packer.onload = () => {
            //img, x, y, width, height
            this.ctx.drawImage(packer, leftX, leftY, 15, 30);
            this.ctx.drawImage(packer, rightX, rightY, 15, 30);

        };

        packer.src = "../../../assets/images/SVG/packer.svg";
    }

    /**
     * TODO: Diagonal packer has different h/w
     * @param leftX
     * @param leftY
     * @param rightX
     * @param rightY
     */
    addDiagonalPacker(leftX: number, leftY: number, rightX: number, rightY: number): void {
        let packer = new Image();
        packer.onload = () => {
            //img, x, y, width, height
            this.ctx.drawImage(packer, leftX, leftY, 27, 34);
            this.ctx.drawImage(packer, rightX, rightY, 27, 34);
        }
        packer.src = "../../../assets/images/SVG/diagonal-packer.svg";
    }

    /**
     * TODO: Make this the standard for shoe placement
     * @param leftX
     * @param leftY
     * @param rightX
     * @param rightY
     */
    addDirectionalVerticalShoe(leftX: number, leftY: number, rightX: number, rightY: number): void {
        let shoeLeft = new Image();
        let shoeRight = new Image();

        shoeLeft.onload = () => {
            //img, x, y, width, height
            this.ctx.drawImage(shoeLeft, leftX, leftY, this.shoeHW, this.shoeHW);
        };

        shoeRight.onload = () => {
            //img, x, y, width, height
            this.ctx.drawImage(shoeRight, rightX, rightY, this.shoeHW, this.shoeHW);
        };

        shoeLeft.src = "../../../assets/images/SVG/shoe-left.svg";
        shoeRight.src = "../../../assets/images/SVG/shoe-right.svg";
    }

    /**
     * Diagonal shoes have different h/w
     * @param leftX
     * @param leftY
     * @param rightX
     * @param rightY
     */
    addDiagonalShoe(leftX: number, leftY: number, rightX: number, rightY:number): void {

        let shoeLeft = new Image();
        let shoeRight = new Image();

        shoeLeft.onload = () => {
            //img, x, y, width, height
            this.ctx.drawImage(shoeLeft, leftX, leftY, 19, 24);
        };

        shoeRight.onload = () => {
            //img, x, y, width, height
            this.ctx.drawImage(shoeRight, rightX, rightY, 26, 17);
        };

        shoeLeft.src = "../../../assets/images/SVG/diagonal-shoe-left.svg";
        shoeRight.src = "../../../assets/images/SVG/diagonal-shoe-right.svg";

    }

    /**
     * Uses a different (more logical input order), need to make this the standard
     * @param leftX
     * @param leftY
     * @param rightX
     * @param rightY
     */
    addDirectionalVerticalCollar(leftX: number, leftY: number, rightX: number, rightY: number): void {
        let collar = new Image();
        collar.onload = () => {
            //img, x, y, width, height
            this.ctx.drawImage(collar, leftX, leftY, 30, 15);
            this.ctx.drawImage(collar, rightX, rightY, 30, 15);

        };

        collar.src = "../../../assets/images/SVG/collar-solid.svg";
    }

    /**
     * Diagonal collars have different h/w
     * @param leftX
     * @param leftY
     * @param rightX
     * @param rightY
     */
    addDiagonalCollars(leftX: number, leftY: number, rightX: number, rightY: number): void {
        let collar = new Image();
        collar.onload = () => {
            //img, x, y, width, height
            this.ctx.drawImage(collar, leftX, leftY, 28, 22);
            this.ctx.drawImage(collar, rightX, rightY, 28, 22);
        };
        collar.src = "../../../assets/images/SVG/diagonal-collar-solid.svg";
    }

    /**
     * Length of property past the kickpoint
     * Differences are due to the desire to put different icons at the bottom of the string
     * @param property
     * @param top
     */
    getPostKickPointSegmentLength(property: string, top:boolean = false): number {
        let length = 0;
        const bottom = this.hasBorehole(this.segment) ? this.segment.boreholeBottom : this.segment.casingBottom;
        let bottomFeet = bottom - this.firstKickpoint;
        let bottomPixels = this.canvasHeight - this.canvasCenterY;
        let bottomPixelsPerFoot = bottomPixels / bottomFeet;

        switch (property) {
            case "cement":
                let cementValue = this.segment.cementBottom - this.firstKickpoint;
                if (top) { cementValue = this.segment.cementTop - this.firstKickpoint; }
                return cementValue * bottomPixelsPerFoot;
            case "borehole":
                return ((this.segment.boreholeBottom - this.segment.boreholeTop) * this.factorFeetToPixels) + this.canvasMarginTop - this.canvasCenterY;
            case "casing":
                let casingValue = this.segment.casingBottom - this.firstKickpoint;
                if (top) { casingValue = this.segment.casingTop - this.firstKickpoint; }
                return casingValue * bottomPixelsPerFoot;
            case "shoe":
                //-21 - puts shoes BOTTOM at bottom of string when 100% height
                return ((this.segment.casingShoeDepth - this.firstKickpoint) * bottomPixelsPerFoot) - 21;
            case "collar":
                //-11 - puts collar middle at the bottom of string when 100% height
                return ((this.segment.floatCollarDepth - this.firstKickpoint) * bottomPixelsPerFoot) - 11;
            case "packer":
                //-24 - puts packer middle at bottom of the string when 100% height
                return ((this.segment.packerDepth - this.firstKickpoint) * bottomPixelsPerFoot) - 24;
            case "basket":
                //-5 - puts basket middle at bottom on the string when 100% height
                return ((this.segment.basketDepth - this.firstKickpoint) * bottomPixelsPerFoot) - 5;
            default:
                console.log("missed case in getPostKickPointSegmentLength " + property);
                return -1;
        }

    }

    /**
     * Difference in "X" based on the length past the kickpoint
     * @param postKickPointCementLength
     */
    getLeftBottomByPostKPLength(postKickPointCementLength): number {
        //difference in x value over the length of the string post kick point
        return postKickPointCementLength * Math.sin(0.418879);
    }

    /**
     * Converts degrees to radians
     *  Tangent function uses radians
     * @param degrees
     */
    degressToRadians(degrees: number) {
        return degrees * (Math.PI / 180);
    }

    /**
     * Draws black lines at the string bottom
     * */
    addSegmentBottom(): void {

        //bottom is always in the same location
        let leftX = this.leftBorehole + this.boreholeWidth - 20;
        let leftY = this.canvasHeight + 10;
        let rightX = this.rightBorehole + this.boreholeWidth + 10;
        let rightY = this.canvasHeight - 43;

        /*
        this.drawPoint(leftX, leftY, 'red');
        this.drawPoint(rightX, rightY, 'blue');
        */

        this.ctx.beginPath();
        this.ctx.lineWidth = 1;
        this.ctx.strokeStyle = "#000000";
        this.ctx.moveTo(leftX, leftY);
        this.ctx.lineTo(rightX, rightY);
        this.ctx.stroke();
        this.ctx.closePath();
        
    }

    /**
     * Filled cement at string bottom
     * ALWAYS in the same location
     * */
    addCementBottom(): void {
        let postKickPointPackerLength = this.getPostKickPointSegmentLength("borehole");
        let leftBottom = this.getLeftBottomByPostKPLength(postKickPointPackerLength);

       
        this.ctx.strokeStyle = this.cementFillColor;
        this.ctx.lineWidth = this.cementLineWidthPx;

        let x1 = this.leftBorehole + leftBottom + 15;
        let y1 = this.canvasHeight - 13;
        let x2 = x1 + (this.rightCasing + this.innerCasingLineWidth) - this.leftCasing;
        let y2 = y1 - this.cementLineWidthPx - 6;

        this.ctx.beginPath();

        this.ctx.moveTo(x1, y1);
        this.ctx.lineTo(x2, y2);
        this.ctx.stroke();
        this.ctx.closePath();
    }

}